import { ApplicationRef, Component, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ThemeService } from 'amatis-ui';
import { LoadingService } from './core/services/loading/loading.service';
import { LoadingComponent } from './shared/components/loading/loading/loading.component';
import { ToastComponent } from './shared/components/toast/toast/toast.component';
import { LanguageService } from './shared/services/language.service';
import { environment } from '../environments/environment';
import { UserService } from './core/services/user/user.service';
import { GetLanguageForUserModel } from './core/models/user/get-language-for-user.model';
import { BaseApiResponse } from './core/models/api/api.model';
import { AsyncPipe, CommonModule } from '@angular/common';
import { VideoCallModalComponent } from './shared/components/modal/video-call-modal/video-call-modal.component';
import { CallOverlayService } from './core/services/call-overlay/call-overlay.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first, concat, interval } from 'rxjs';
import { VersionService } from './core/services/version/version.service';
import { TranslateService } from '@ngx-translate/core';
import { GroupCallOverlayService } from './core/services/call-overlay/group-call-overlay.service';
import { GroupCallComponent } from './shared/components/group-call/group-call-room/group-call.component';
import {
  ThemePaletteOption,
  ThemePaletteService,
} from './shared/services/theme-palette.service';
import { CurrentUserService } from './core/services/auth/current-user.service';
import { DEFAULT_PALETTE_ID } from './shared/config/brand-palette.config';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    ToastComponent,
    LoadingComponent,
    AsyncPipe,
    VideoCallModalComponent,
    GroupCallComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'arpm';
  private readonly defaultPaletteId = DEFAULT_PALETTE_ID;
  paletteOptions: ThemePaletteOption[] = [];
  selectedPaletteId: string = this.defaultPaletteId;
  showPaletteModal = false;

  constructor(
    private readonly themeService: ThemeService,
    private readonly languageService: LanguageService,
    public loadingService: LoadingService,
    private userService: UserService,
    public callOverlay: CallOverlayService,
    public groupOverlay: GroupCallOverlayService,
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private versionService: VersionService,
    private translate: TranslateService,
    private router: Router,
    private currentUserService: CurrentUserService,
    private themePaletteService: ThemePaletteService
  ) {}

  ngOnInit() {
    this.themeService.initTheme();
    if (!localStorage.getItem('themeInitialized')) {
      this.themeService.setTheme('dark'); // varsayılan: dark
      localStorage.setItem('themeInitialized', '1'); // bir daha ezme
    }

    const savedLang = localStorage.getItem('app-lang');
    if (!savedLang) {
      this.getLanguage();
    } else {
      this.languageService.initLanguage();
    }

    this.initVersionControl();
    this.initPaletteFlow();
  }

  getLanguage() {
    this.userService.getLanguageForUser().subscribe({
      next: (res: BaseApiResponse<GetLanguageForUserModel>) => {
        const lang = res.data.language || environment.defaultLanguage;
        this.languageService.setLanguageFromBackend(lang);
      },
      error: () => {
        this.languageService.initLanguage();
      },
    });
  }

  private initVersionControl(): void {
    this.versionService.checkVersionFromBackend().subscribe({
      next: (versionChanged) => {
        if (versionChanged) {
          return;
        }

        this.initServiceWorkerUpdates();
      },
      error: (error) => {
        console.error('[AppComponent] Version check error:', error);
        this.initServiceWorkerUpdates();
      },
    });
  }

  private initServiceWorkerUpdates(): void {
    if (this.swUpdate.isEnabled) {
      console.log('[AppComponent] Service Worker is enabled');

      this.swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'
          )
        )
        .subscribe((evt) => {
          console.log('[AppComponent] New version ready:', evt);

          const message = this.translate.instant(
            'serviceWorker.newVersionAvailable'
          );

          if (confirm(message)) {
            this.swUpdate.activateUpdate().then(() => {
              console.log('[AppComponent] Update activated, reloading...');
              window.location.reload();
            });
          }
        });

      const appIsStable$ = this.appRef.isStable.pipe(
        first((isStable) => isStable === true)
      );
      const every30Minutes$ = interval(30 * 60 * 1000);
      const every30MinutesOnceAppIsStable$ = concat(
        appIsStable$,
        every30Minutes$
      );

      every30MinutesOnceAppIsStable$.subscribe(() => {
        console.log(this.translate.instant('serviceWorker.checkingForUpdates'));
        this.swUpdate.checkForUpdate().then((updateFound) => {
          if (updateFound) {
            console.log('[AppComponent] Update found!');
          } else {
            console.log('[AppComponent] No update found');
          }
        });
      });

      this.swUpdate.unrecoverable.subscribe((event) => {
        console.error(
          '[AppComponent] Service Worker unrecoverable error:',
          event.reason
        );
        if (confirm(this.translate.instant('serviceWorker.updateError'))) {
          window.location.reload();
        }
      });
    } else {
      console.log('[AppComponent] Service Worker is not enabled');
    }
  }

  private initPaletteFlow(): void {
    this.paletteOptions = this.themePaletteService.palettes;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        void this.evaluatePaletteState();
      });

    void this.evaluatePaletteState();
  }

  private async evaluatePaletteState(): Promise<void> {
    const isAuthRoute = this.router.url.startsWith('/auth');
    const isPatientMonitorRoute =
      this.router.url.startsWith('/patient-monitor');
    if (isAuthRoute || isPatientMonitorRoute) {
      this.showPaletteModal = false;
      return;
    }

    const session = this.currentUserService.GetSession();
    const userId = session?.id;

    if (!userId) {
      this.themePaletteService.applyPaletteById(this.defaultPaletteId);
      this.showPaletteModal = false;
      return;
    }

    await this.themePaletteService.ensurePalettesLoaded();
    this.paletteOptions = this.themePaletteService.palettes;

    const sessionPalette = this.resolveSessionPalette(session);
    if (sessionPalette) {
      this.themePaletteService.applyUserPalette(userId, sessionPalette);
      this.showPaletteModal = false;
      return;
    }

    this.themePaletteService.applySavedPalette(userId);

    this.showPaletteModal = !this.themePaletteService.hasSelection(userId);
    if (this.showPaletteModal) {
      this.selectedPaletteId =
        this.paletteOptions.find((p) => p.id === this.defaultPaletteId)?.id ??
        this.paletteOptions[0]?.id ??
        this.defaultPaletteId;
    }
  }

  selectPalette(paletteId: string): void {
    this.selectedPaletteId = paletteId;
    this.themePaletteService.applyPaletteById(paletteId);
  }

  async confirmPaletteSelection(): Promise<void> {
    const session = this.currentUserService.GetSession();
    const userId = session?.id;
    if (!userId) return;

    await this.themePaletteService.addPaletteAndApply(
      userId,
      this.selectedPaletteId
    );
    this.showPaletteModal = false;
  }

  private resolveSessionPalette(session: any): ThemePaletteOption | null {
    const raw = session?.colorPalette ?? session?.ColorPalette;
    return this.themePaletteService.mapApiPalette(raw);
  }
}
